import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import useOAuthPopup from '../../hooks/useOAuthPopup.js';
import AIConfigurationTab from './AIConfigurationTab';
import CampaignPreferencesTab from './CampaignPreferencesTab';
import LlmPricingTab from './LlmPricingTab';
import {
  AD_ACCOUNT,
  PAGE_TOGGLE_ERROR,
  REMOVAL_ERROR_META,
  SELECT_ERROR,
  applySelection,
  buildAdAccountRows,
  buildPageRows,
  clearRemovedKeys,
  metaConfirmBody,
  metaConfirmTitle,
  metaItemTypeLabel,
  metaKeepLabel,
  metaRemoveCtaLabel,
  removalSuccessToast,
  sectionSelectAllLabel,
  selectSuccessToast,
  selectedAccountNote,
  selectionLabel,
  toMetaRemovalPayload,
  toggleKey,
  toggleSection,
} from '../../utils/metaAdsSelection.js';

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

const jsonHeaders = () => ({ Accept: 'application/json', 'X-CSRF-Token': csrfToken() });

// ── "Ad Accounts & Pages" building blocks ───────────────────────────────────────────────────
// Presentational only, mirroring the shipped Social Media AI settings tab: these render what
// they are handed, every decision lives in utils/metaAdsSelection.js, and all state/fetching
// lives in SettingsModal below. Copies rather than extractions on purpose — SocialMediaAIPage.js
// is in production and this repo has no React render-test harness to catch a shared-component
// regression. The layer where visual drift would actually show (the `.ap-*` CSS) IS shared.

// A single-line name that shows a styled tooltip ONLY when the text is actually clipped.
// `title` stays on the element as the accessibility/no-JS fallback; the bubble is the visual one.
const ClippedName = ({ text, className }) => {
  const ref = useRef(null);
  const [tip, setTip] = useState(null);

  const show = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // The whole point: a name that fits gets no tooltip at all.
    if (el.scrollWidth <= el.clientWidth) return;
    const rect = el.getBoundingClientRect();
    setTip({ left: rect.left + (rect.width / 2), top: rect.top - 8 });
  }, []);

  const hide = useCallback(() => setTip(null), []);

  return (
    <React.Fragment>
      <span
        ref={ref}
        className={className}
        title={text}
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        {text}
      </span>
      {tip && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div className="ap-name-tip" role="tooltip" style={{ left: tip.left, top: tip.top }}>{text}</div>,
        document.body
      )}
    </React.Fragment>
  );
};

const SectionEmptyState = ({ title, body, ctaLabel, onCta }) => (
  <div className="ap-empty">
    <h4>{title}</h4>
    <p>{body}</p>
    <button type="button" className="ap-btn ap-btn--primary" onClick={onCta}>{ctaLabel}</button>
  </div>
);

// A failed GET must never be dressed up as "you have nothing connected" — that invites the user
// to reconnect something that is already there. Each section owns its own bar and its own retry.
const SectionErrorBar = ({ message, onRetry }) => (
  <div className="ap-errorbar" role="alert">
    <span>{message}</span>
    <button type="button" className="ap-textbtn" onClick={onRetry}>Try again</button>
  </div>
);

// Ad accounts are a RADIO group: Company#selected_ad_account resolves to exactly one row, so the
// selected account gets a static pill and every other row gets the button that would claim it.
// (Pages, below, are the opposite — several can be active at once.)
const AdAccountRow = ({ row, checked, onCheck, onSelect, onRemove, selecting }) => {
  const className = ['ap-row',
    checked ? 'is-selected' : '',
    row.selected ? 'is-active' : ''].filter(Boolean).join(' ');

  return (
    <div className={className}>
      <input
        type="checkbox"
        className="ap-check"
        checked={checked}
        onChange={() => onCheck(row.key)}
        aria-label={`Select ${row.name}`}
      />
      <span className="ap-rowtext">
        <ClippedName className="ap-rowname" text={row.name} />
        <span className="ap-rowmeta">{row.meta}</span>
      </span>

      {row.selected ? (
        <span
          className="ap-pill ap-pill--connected"
          role="radio"
          aria-checked="true"
          title="New campaigns are created in this ad account."
        >
          <span className="ap-pill-dot" />
          Selected
        </span>
      ) : (
        <button
          type="button"
          className="ap-pill ap-pill--setactive"
          role="radio"
          aria-checked="false"
          onClick={() => onSelect(row)}
          disabled={selecting}
          title="Create new campaigns in this ad account instead."
        >
          {selecting ? 'Saving…' : 'Use this account'}
        </button>
      )}

      <button
        type="button"
        className="ap-x"
        onClick={() => onRemove(row)}
        title={`Remove ${row.name}`}
        aria-label={`Remove ${row.name}`}
      >
        ✕
      </button>
    </div>
  );
};

const PageRow = ({ row, checked, onCheck, onToggleActive, onRemove, toggling }) => {
  const className = ['ap-row',
    checked ? 'is-selected' : '',
    row.active ? 'is-active' : ''].filter(Boolean).join(' ');

  return (
    <div className={className}>
      <input
        type="checkbox"
        className="ap-check"
        checked={checked}
        onChange={() => onCheck(row.key)}
        aria-label={`Select ${row.name}`}
      />
      <span className="ap-rowtext">
        <ClippedName className="ap-rowname" text={row.name} />
        <span className="ap-rowmeta">{row.meta}</span>
      </span>

      <button
        type="button"
        className={`ap-pill ${row.active ? 'ap-pill--connected' : 'ap-pill--setactive'}`}
        onClick={() => onToggleActive(row.id)}
        disabled={toggling}
        aria-pressed={row.active}
        title={row.active
          ? 'Active — ads can run from this Page. Click to deactivate.'
          : 'Activate this Page so ads can run from it.'}
      >
        {toggling ? 'Saving…' : (row.active ? (
          <React.Fragment>
            <span className="ap-pill-dot" />
            <span className="ap-pill-label--rest">Active</span>
            <span className="ap-pill-label--hover">Deactivate</span>
          </React.Fragment>
        ) : 'Set as active')}
      </button>

      <button
        type="button"
        className="ap-x"
        onClick={() => onRemove(row)}
        title={`Remove ${row.name}`}
        aria-label={`Remove ${row.name}`}
      >
        ✕
      </button>
    </div>
  );
};

// THE confirmation for every destructive action in this modal. The per-row ✕, the bulk bar, the
// LLM-pricing delete and the AI-config Reset All all open THIS dialog with an already-built item
// list — there is never a second confirm behind it, and never a window.confirm/alert.
//
// Exported (and imported by LlmPricingTab/AIConfigurationTab, which this file also imports). The
// import cycle is safe: the binding is only read inside a render, long after both modules have
// finished evaluating.
export const ConfirmDialog = ({
  title,
  body,
  items,
  note,
  confirmLabel,
  cancelLabel,
  busyLabel,
  danger,
  busy,
  onCancel,
  onConfirm,
}) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  return (
    <div className="ap-confirm-overlay" onClick={busy ? undefined : onCancel}>
      <div
        className="ap-confirm"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <h3>{title}</h3>
        <p>{body}</p>

        {Array.isArray(items) && items.length > 0 && (
          <div className="ap-confirm-list">
            {items.map((row) => (
              <div className="ap-confirm-item" key={row.key}>
                <span className="ap-confirm-item-name">{row.name}</span>
                <span className="ap-confirm-item-meta">{metaItemTypeLabel(row)}</span>
              </div>
            ))}
          </div>
        )}

        {note && <div className="ap-confirm-note">{note}</div>}

        <div className="ap-confirm-actions">
          <button type="button" className="ap-btn ap-btn--quiet" onClick={onCancel} disabled={busy}>
            {cancelLabel || 'Cancel'}
          </button>
          <button
            type="button"
            className={`ap-btn ${danger ? 'ap-btn--danger' : 'ap-btn--primary'}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? (busyLabel || 'Removing…') : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// Settings Modal Component
const SettingsModal = ({ isOpen, onClose, isAdmin, companySlug }) => {
  // Helper: append company_slug to API URLs for per-tab company scoping
  const scopeUrl = (url) => {
    if (!companySlug) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}company_slug=${companySlug}`;
  };
  const reactRoot = document.getElementById('react-root');
  const facebookConnectedAttr = reactRoot?.getAttribute('facebook-connected') === 'true';
  const [activeTab, setActiveTab] = useState('accounts');
  const [adsMediaAssetsUrl, setAdsMediaAssetsUrl] = useState('');

  // Row state, not raw payloads: the selection pill and the Page toggle both update optimistically
  // and are reconciled from the write's own response, so rows have to be the thing that moves.
  const [adAccountRows, setAdAccountRows] = useState([]);
  const [pageRows, setPageRows] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [pagesLoaded, setPagesLoaded] = useState(false);
  const [accountsError, setAccountsError] = useState(null);
  const [pagesError, setPagesError] = useState(null);

  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  // null, or the exact list of rows awaiting THE one confirmation.
  const [confirmItems, setConfirmItems] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [selectingKey, setSelectingKey] = useState(null);
  const [togglingPageId, setTogglingPageId] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    const fromDom = reactRoot?.getAttribute('ads-media-assets-url') || '';
    setAdsMediaAssetsUrl(fromDom);
  }, [isOpen]);

  // Out-of-scope code (MetaAdsAIPage's gates, the server-rendered header) still reads this
  // attribute, and nothing else refreshes it now that this modal never reloads the page.
  const mirrorHasAdAccounts = (connected) => {
    try { reactRoot?.setAttribute('has-ad-accounts', String(connected)); } catch (_e) { /* attr is best-effort */ }
  };

  const loadAdAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      const res = await fetch(scopeUrl('/api/facebook/ad_accounts_status'), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error('ad_accounts_status request failed');
      const data = await res.json();
      const rows = buildAdAccountRows(data?.ad_accounts);
      setAdAccountRows(rows);
      mirrorHasAdAccounts(rows.length > 0);
      setAccountsError(null);
      setAccountsLoaded(true);
    } catch (_e) {
      setAccountsError("We couldn't load your ad accounts. Check your connection and try again.");
    } finally {
      setAccountsLoading(false);
    }
  }, [companySlug]);

  const loadPages = useCallback(async () => {
    setPagesLoading(true);
    try {
      const res = await fetch(scopeUrl('/api/facebook/connection_status'), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error('connection_status request failed');
      const data = await res.json();
      // Null Instagram payload on purpose: this modal is Facebook-only and buildPageRows
      // degrades to Facebook Pages when the IG side is absent.
      setPageRows(buildPageRows(data, null));
      setPagesError(null);
      setPagesLoaded(true);
    } catch (_e) {
      setPagesError("We couldn't load your Pages. Check your connection and try again.");
    } finally {
      setPagesLoading(false);
    }
  }, [companySlug]);

  // Load once per modal open, the first time the merged tab is shown. Deliberately NOT gated on
  // the `has-ad-accounts` attribute: that is whatever Rails stamped at page load, and a connect
  // that just happened is exactly the case it gets wrong.
  const bootedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) { bootedRef.current = false; return; }
    if (activeTab !== 'accounts' || bootedRef.current) return;
    bootedRef.current = true;
    loadAdAccounts();
    loadPages();
  }, [isOpen, activeTab, loadAdAccounts, loadPages]);

  // The modal is rendered unconditionally by the page and early-returns when closed, so transient
  // UI state would otherwise outlive it — reopening onto a stale tick for a row that has since
  // been removed makes "Remove all N" a silent no-op.
  useEffect(() => {
    if (isOpen) return;
    setSelectedKeys(new Set());
    setConfirmItems(null);
  }, [isOpen]);

  // useOAuthPopup toasts the popup's own result (:72-79) and swallows callback throws (:81), so
  // this adds no toast of its own and each loader owns its inline error state.
  const refreshMetaAdsConnections = useCallback(() => {
    loadAdAccounts();
    loadPages();
  }, [loadAdAccounts, loadPages]);

  const openOAuthPopup = useOAuthPopup(refreshMetaAdsConnections);

  const openMetaAdsPopup = () => openOAuthPopup('/facebook/oauth/authorize_meta_ads?popup=1', 'metaAdsAuth');
  const openFacebookPopup = () => openOAuthPopup('/facebook/oauth/authorize_meta_ads?popup=1', 'fbAuth');

  // ── Selection (checkboxes) ────────────────────────────────────────────────────────────────
  const handleCheck = useCallback((key) => {
    setSelectedKeys((prev) => toggleKey(prev, key));
  }, []);

  const selectedCount = selectedKeys.size;

  // Both removal paths funnel through here, so there is exactly ONE confirmation for either. No
  // cascade exists in this modal (the Facebook login lives in the Social settings modal), so the
  // dialog lists precisely what was ticked.
  const openConfirmFor = useCallback((keys) => {
    const wanted = keys instanceof Set ? keys : new Set(keys);
    const items = [...adAccountRows, ...pageRows].filter((row) => wanted.has(row.key));
    if (items.length === 0) return;
    setConfirmItems(items);
  }, [adAccountRows, pageRows]);

  const handleRowRemove = useCallback((row) => openConfirmFor([row.key]), [openConfirmFor]);
  const handleBulkRemove = useCallback(() => openConfirmFor(selectedKeys), [openConfirmFor, selectedKeys]);

  // Removing the selected ad account silently retargets every future campaign, so the successor —
  // the lowest-id active account left standing, which is exactly what Company#selected_ad_account
  // would have fallen through to — is named out loud before the user confirms.
  const confirmNote = useMemo(() => {
    if (!confirmItems) return null;
    const removedSelected = confirmItems.find((row) => row.type === AD_ACCOUNT && row.selected);
    if (!removedSelected) return null;
    const removedKeys = new Set(confirmItems.map((row) => row.key));
    const successor = adAccountRows
      .filter((row) => !removedKeys.has(row.key))
      .slice()
      .sort((a, b) => a.pk - b.pk)[0];
    return selectedAccountNote(removedSelected.name, successor ? successor.name : null);
  }, [confirmItems, adAccountRows]);

  // ONE request, all-or-nothing (the server wraps the whole payload in a transaction). The
  // response carries the post-write state of both lists, so state is replaced from it — no
  // follow-up fetch, no window in which the two could disagree.
  const performRemoval = useCallback(async () => {
    if (!confirmItems) return;
    const items = confirmItems;
    setRemoving(true);
    try {
      const res = await fetch(scopeUrl('/api/facebook/connections'), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...jsonHeaders() },
        credentials: 'same-origin',
        body: JSON.stringify({ connections: toMetaRemovalPayload(items) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || 'bulk remove failed');

      const nextAccountRows = buildAdAccountRows(data.ad_accounts);
      setAdAccountRows(nextAccountRows);
      setAccountsError(null);
      setAccountsLoaded(true);
      if (data.facebook) {
        setPageRows(buildPageRows(data.facebook, null));
        setPagesError(null);
        setPagesLoaded(true);
      }
      mirrorHasAdAccounts(nextAccountRows.length > 0);
      // Only the rows that actually went lose their tick; anything else stays selected.
      setSelectedKeys((prev) => clearRemovedKeys(prev, items));

      const removedSelected = items.some((row) => row.type === AD_ACCOUNT && row.selected);
      const promoted = removedSelected ? nextAccountRows.find((row) => row.selected) : null;
      toast.success(removalSuccessToast({ ...data, promotedName: promoted ? promoted.name : null }));
      setConfirmItems(null);
    } catch (_e) {
      // Nothing was removed. The dialog stays open on the same items so the user can retry the
      // action they already confirmed, and both lists stay exactly as they were.
      toast.error(REMOVAL_ERROR_META);
    } finally {
      setRemoving(false);
    }
  }, [confirmItems, companySlug]);

  // Single-select. Optimistic because the server side is a clear-then-set in one transaction and
  // the endpoint is idempotent; re-clicking the selected row issues no request at all.
  const handleSelectAccount = useCallback(async (row) => {
    if (row.selected) return;
    const previousRows = adAccountRows;
    setAdAccountRows((prev) => applySelection(prev, row.key));
    setSelectingKey(row.key);
    try {
      const res = await fetch(scopeUrl(`/api/facebook/ad_accounts/${row.pk}/select`), {
        method: 'PATCH',
        headers: jsonHeaders(),
        credentials: 'same-origin',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error('select failed');
      if (Array.isArray(data.ad_accounts)) setAdAccountRows(buildAdAccountRows(data.ad_accounts));
      toast.success(selectSuccessToast(row.name));
    } catch (_e) {
      setAdAccountRows(previousRows);
      toast.error(SELECT_ERROR);
    } finally {
      setSelectingKey(null);
    }
  }, [adAccountRows, companySlug]);

  // Activate / deactivate a Page. Success is silent — the pill IS the feedback.
  const handleTogglePage = useCallback(async (pageId) => {
    const previousRows = pageRows;
    setPageRows((prev) => prev.map((p) => (p.id === pageId ? { ...p, active: !p.active } : p)));
    setTogglingPageId(pageId);
    try {
      const res = await fetch(scopeUrl(`/api/facebook/pages/${pageId}/toggle`), {
        method: 'PATCH',
        headers: jsonHeaders(),
        credentials: 'same-origin',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error('toggle failed');
      const nowActive = data.page?.active;
      if (typeof nowActive === 'boolean') {
        setPageRows((prev) => prev.map((p) => (p.id === pageId ? { ...p, active: nowActive } : p)));
      }
    } catch (_e) {
      setPageRows(previousRows);
      toast.error(PAGE_TOGGLE_ERROR);
    } finally {
      setTogglingPageId(null);
    }
  }, [pageRows, companySlug]);

  const handleSaveAdsMediaAssets = async () => {
    const companyId = reactRoot?.getAttribute('company-id');
    if (!companyId) {
      toast.error('Company not found.');
      return;
    }
    try {
      const res = await fetch(`/companies/${companyId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...jsonHeaders() },
        credentials: 'same-origin',
        body: JSON.stringify({
          company: { ads_media_assets_url: adsMediaAssetsUrl || '' }
        })
      });
      if (res.ok || res.status === 201) {
        try { reactRoot?.setAttribute('ads-media-assets-url', adsMediaAssetsUrl || ''); } catch (_e) { /* attr is best-effort */ }
        toast.success('Settings saved.');
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || data.message || 'Failed to save settings');
      }
    } catch (e) {
      toast.error('Failed to save settings');
    }
  };

  // Nothing connected AND no Facebook login: the ad-account and Page CTAs would both dead-end at
  // the same "connect Facebook first" wall, so show that wall once instead of two empty states.
  const showConnectPrompt = accountsLoaded && pagesLoaded
    && adAccountRows.length === 0 && pageRows.length === 0
    && !accountsError && !pagesError
    && !facebookConnectedAttr;

  if (!isOpen) return null;

  return (
    <div className="modal-overlay meta-ads-settings-modal" onClick={onClose}>
      <div className="modal-container meta-ads-settings-container" onClick={(e) => e.stopPropagation()}>
        <div className="meta-ads-settings-header">
          <h2>Settings</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="meta-ads-settings-contents">
          <div className="settings-tabs">
            <button
              type="button"
              className={`settings-tab ${activeTab === 'accounts' ? 'active' : ''}`}
              onClick={() => setActiveTab('accounts')}
            >
              Ad Accounts &amp; Pages
            </button>
            <button
              type="button"
              className={`settings-tab ${activeTab === 'preferences' ? 'active' : ''}`}
              onClick={() => setActiveTab('preferences')}
            >
              Campaign Preferences
            </button>
            <button
              type="button"
              className={`settings-tab ${activeTab === 'media-assets' ? 'active' : ''}`}
              onClick={() => setActiveTab('media-assets')}
            >
              Media Assets
            </button>
            {isAdmin && (
              <button
                type="button"
                className={`settings-tab ${activeTab === 'ai-config' ? 'active' : ''}`}
                onClick={() => setActiveTab('ai-config')}
              >
                AI Configuration<span className="admin-only-dot" />
              </button>
            )}
            {isAdmin && (
              <button
                type="button"
                className={`settings-tab ${activeTab === 'llm-pricing' ? 'active' : ''}`}
                onClick={() => setActiveTab('llm-pricing')}
              >
                LLM Pricing<span className="admin-only-dot" />
              </button>
            )}
          </div>

          {activeTab === 'accounts' && (
            <div className="connect-page-content ap-tab">
              {showConnectPrompt ? (
                <div className="ap-empty">
                  <h4>Connect Facebook to get started</h4>
                  <p>Connect your Facebook account first to access Meta Ads features.</p>
                  <button type="button" className="ap-btn ap-btn--primary" onClick={openMetaAdsPopup}>
                    Connect Facebook
                  </button>
                </div>
              ) : (
                <React.Fragment>
                  {/* ── Section 1: where the money is spent ── */}
                  <div className="ap-section">
                    <div className="ap-section-head">
                      <div>
                        <h3 className="ap-section-title">Ad Accounts</h3>
                        <p className="ap-section-sub">The Meta ad account Vesta creates and syncs campaigns in.</p>
                      </div>
                      <div className="ap-section-actions">
                        {adAccountRows.length > 0 && (
                          <button
                            type="button"
                            className="ap-textbtn"
                            onClick={() => setSelectedKeys((prev) => toggleSection(prev, adAccountRows))}
                          >
                            {sectionSelectAllLabel(selectedKeys, adAccountRows)}
                          </button>
                        )}
                        <button type="button" className="ap-btn ap-btn--outline" onClick={openMetaAdsPopup}>
                          <span aria-hidden="true">+</span> Connect Ad Account
                        </button>
                      </div>
                    </div>

                    {accountsError && (
                      <SectionErrorBar message={accountsError} onRetry={loadAdAccounts} />
                    )}

                    {accountsLoading && !accountsLoaded ? (
                      <div className="ap-loading">Loading your ad accounts…</div>
                    ) : adAccountRows.length === 0 ? (
                      !accountsError && (
                        <SectionEmptyState
                          title="No ad accounts connected"
                          body="Connect the Facebook ad account Vesta should create campaigns in."
                          ctaLabel="Connect Ad Account"
                          onCta={openMetaAdsPopup}
                        />
                      )
                    ) : (
                      <div className="ap-list" role="radiogroup" aria-label="Selected ad account">
                        {adAccountRows.map((row) => (
                          <AdAccountRow
                            key={row.key}
                            row={row}
                            checked={selectedKeys.has(row.key)}
                            onCheck={handleCheck}
                            onSelect={handleSelectAccount}
                            onRemove={handleRowRemove}
                            selecting={selectingKey === row.key}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* ── Section 2: the Pages ads run from ── */}
                  <div className="ap-section">
                    <div className="ap-section-head">
                      <div>
                        <h3 className="ap-section-title">Pages</h3>
                        <p className="ap-section-sub">Set a Page active to run ads from it.</p>
                      </div>
                      <div className="ap-section-actions">
                        {pageRows.length > 0 && (
                          <button
                            type="button"
                            className="ap-textbtn"
                            onClick={() => setSelectedKeys((prev) => toggleSection(prev, pageRows))}
                          >
                            {sectionSelectAllLabel(selectedKeys, pageRows)}
                          </button>
                        )}
                        <button type="button" className="ap-btn ap-btn--outline" onClick={openFacebookPopup}>
                          <span aria-hidden="true">+</span> Connect a Page
                        </button>
                      </div>
                    </div>

                    {pagesError && (
                      <SectionErrorBar message={pagesError} onRetry={loadPages} />
                    )}

                    {pagesLoading && !pagesLoaded ? (
                      <div className="ap-loading">Loading your Pages…</div>
                    ) : pageRows.length === 0 ? (
                      !pagesError && (
                        <SectionEmptyState
                          title="No Pages connected"
                          body="Connect a Facebook Page to run ads from."
                          ctaLabel="Connect a Page"
                          onCta={openFacebookPopup}
                        />
                      )
                    ) : (
                      <div className="ap-list">
                        {pageRows.map((row) => (
                          <PageRow
                            key={row.key}
                            row={row}
                            checked={selectedKeys.has(row.key)}
                            onCheck={handleCheck}
                            onToggleActive={handleTogglePage}
                            onRemove={handleRowRemove}
                            toggling={togglingPageId === row.id}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Bulk action bar — sticks to the bottom of the modal's own scroll area. */}
                  {selectedCount > 0 && (
                    <div className="ap-bar">
                      <span className="ap-bar-count">{selectionLabel(selectedCount)}</span>
                      <button
                        type="button"
                        className="ap-btn ap-btn--ondark"
                        onClick={() => setSelectedKeys(new Set())}
                      >
                        Clear
                      </button>
                      <button type="button" className="ap-btn ap-btn--danger" onClick={handleBulkRemove}>
                        {metaRemoveCtaLabel(selectedCount)}
                      </button>
                    </div>
                  )}
                </React.Fragment>
              )}

              {confirmItems && (
                <ConfirmDialog
                  title={metaConfirmTitle(confirmItems.length)}
                  body={metaConfirmBody(confirmItems)}
                  items={confirmItems}
                  note={confirmNote}
                  cancelLabel={metaKeepLabel(confirmItems.length)}
                  confirmLabel={metaRemoveCtaLabel(confirmItems.length)}
                  busyLabel="Removing…"
                  danger
                  busy={removing}
                  onCancel={() => setConfirmItems(null)}
                  onConfirm={performRemoval}
                />
              )}
            </div>
          )}

          {activeTab === 'preferences' && (
            <div className="connect-page-content">
              <CampaignPreferencesTab isAdmin={isAdmin} companySlug={companySlug} />
            </div>
          )}

          {activeTab === 'media-assets' && (
            <div className="connect-page-content">
              <div>
                <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '8px', display: 'block' }}>
                  Ads Media Assets URL
                </label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <input
                    type="url"
                    className="form-control"
                    placeholder="https://example.com/ads-media-assets"
                    value={adsMediaAssetsUrl}
                    onChange={(e) => setAdsMediaAssetsUrl(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  {adsMediaAssetsUrl && (
                    <button
                      type="button"
                      className="ap-btn ap-btn--outline"
                      onClick={() => {
                        const url = adsMediaAssetsUrl.trim();
                        const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
                        window.open(href, '_blank', 'noopener,noreferrer');
                      }}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Open Link
                    </button>
                  )}
                </div>
                <small className="text-muted" style={{ display: 'block', marginTop: '8px' }}>
                  Enter the URL of media assets folder or collection for Ads
                </small>
                <button
                  type="button"
                  className="ap-btn ap-btn--primary"
                  style={{ marginTop: '12px' }}
                  onClick={handleSaveAdsMediaAssets}
                >
                  Save
                </button>
              </div>
            </div>
          )}

          {activeTab === 'ai-config' && isAdmin && (
            <div className="connect-page-content">
              <AIConfigurationTab companySlug={companySlug} />
            </div>
          )}

          {activeTab === 'llm-pricing' && isAdmin && (
            <div className="connect-page-content">
              <LlmPricingTab />
            </div>
          )}

          {isAdmin && (
            <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb' }}>
              <span className="admin-only-legend">
                <span className="admin-only-dot" />
                Admin only
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
